import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { cn } from '@/lib/utils'

interface MediaImageProps {
    src: string
    alt: string
    sizes: string
    className?: string
    preload?: boolean
}

const OPTIMIZED_HOST = /\.cdn\.modulify\.website$/i

// Images pasted into the CMS from other websites skip optimisation so they can never break a page.
const canOptimize = (src: string): boolean => {
    if (src.startsWith('/')) return true

    try {
        const url = new URL(src)

        return url.protocol === 'https:' && OPTIMIZED_HOST.test(url.hostname)
    }
    catch {
        return false
    }
}

export const MediaImage: FC<MediaImageProps> = ({
    src,
    alt,
    sizes,
    className,
    preload = false
}): ReactNode => {
    if (canOptimize(src)) {
        return (
            <Image
                src={src}
                alt={alt}
                fill
                sizes={sizes}
                preload={preload}
                className={cn('object-cover', className)}
            />
        )
    }

    return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
            src={src}
            alt={alt}
            loading={preload ? 'eager' : 'lazy'}
            decoding='async'
            className={cn('absolute inset-0 size-full object-cover', className)}
        />
    )
}
